import { Box, Chip, Divider, Skeleton, Stack, Typography } from '@mui/material';
import AutocompleteStored from 'components/common/AutocompleteStored';
import TimesheetEditModal from './TimesheetEditModal';
import { durationMinutesToTime } from 'common/utils';
import { addDays, startOfDay } from 'date-fns';
import { Project, Task, Timesheet } from 'axiosApi/models';
import { DaysOfWeek, TimesheetWeekSet, WeekDay } from './TimesheetComponent';
import { getUserEntity } from 'common/UserEntityProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { UUID } from 'crypto';
import { useEffect, useRef, useState } from 'react';

type TimesheetMobileComponentProps = {
    weekDays: WeekDay[];
    selectedDay: number;
    totalWeekDuration: number;
    timesheetSet: TimesheetWeekSet[];
    date: Date;
    isTimesheetLoading: boolean;
    handleProjectChange: (key: any, value: any) => void;
    handleTaskChange: (key: any, value: any) => void;
    addNewRow: () => void;
    refetchTimesheetSet: () => void;
};

export default function TimesheetMobileComponent({
    weekDays,
    selectedDay,
    totalWeekDuration,
    timesheetSet,
    date,
    isTimesheetLoading,
    handleProjectChange,
    handleTaskChange,
    addNewRow,
    refetchTimesheetSet,
}: TimesheetMobileComponentProps) {
    const userDetails = getUserEntity();
    const containerRef = useRef<HTMLDivElement>(null);
    const [boxHeight, setBoxHeight] = useState<number | undefined>(undefined);

    const calculateHeight = () => {
        if (containerRef.current) {
            const parentHeight = containerRef.current.parentElement?.clientHeight || 0;
            const parentPadding = 40 * 2;
            const siblingElements = containerRef.current.parentElement?.children || [];
            let otherElementsHeight = 0;

            Array.from(siblingElements).forEach((element) => {
                if (element !== containerRef.current) {
                    otherElementsHeight += element.clientHeight;
                }
            });

            const availableHeight = parentHeight - otherElementsHeight - parentPadding;
            setBoxHeight(Math.max(200, availableHeight));
        }
    };

    useEffect(() => {
        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return () => {
            window.removeEventListener('resize', calculateHeight);
        };
    }, []);

    const getUsedTaskIds = (currentUuid?: UUID): number[] => {
        return timesheetSet
        .filter(([rowKey]) => rowKey.uuId !== currentUuid && rowKey.idTask !== undefined)
        .map(([rowKey]) => rowKey.idTask);
    };

    return (<Box display={"flex"} flexDirection={"column"} sx={{ width: '100%', flexGrow: 1 }} ref={containerRef}>
        {weekDays[selectedDay] && (
            <Box key={`ts_d_${selectedDay}`} display={"flex"} justifyContent={"space-between"} alignItems={"center"} sx={{ mb: 1 }} px={2}>
                <Typography variant="h2" color="textSecondary" fontWeight={600} style={{ color: weekDays[selectedDay].color }}>
                    {weekDays[selectedDay].day}
                </Typography>
                <Typography variant="h2" color="textSecondary" fontWeight={600}>
                    {durationMinutesToTime(weekDays[selectedDay].duration)}
                </Typography>
            </Box>
        )}
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} px={2} pb={1}>
            <Typography variant="h6" color="textSecondary">
            This week
            </Typography>
            <Typography variant="h6" fontWeight={600} color="textSecondary">
            {durationMinutesToTime(totalWeekDuration)}
            </Typography>
        </Box>
        <Box
            id="timesheet-table"
            sx={{
                flexGrow: 1,
                height: boxHeight ? `${boxHeight}px` : 'auto',
                overflowY: 'auto',
                p: 2,
                boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.2)',
            }}
        >
            {timesheetSet?.map(([key, timesheetRow]) => {

                const currentDate = startOfDay(addDays(date, selectedDay));
                const currentTimesheet: Timesheet = timesheetRow[DaysOfWeek[selectedDay]] || {
                idEmployee: userDetails?.entityId,
                idTask: key.idTask,
                date: currentDate,
                startTime: currentDate,
                task: {
                    ...timesheetRow?.task,
                    job:{
                    ...timesheetRow?.task?.job,
                    project: timesheetRow?.project
                    }
                },
                };
                return (<>
                <Stack key={key.uuId?key.uuId:`${key.idProject}-${key.idTask}`} gap={2} my={2}>
                    <AutocompleteStored
                        label={intl.get("timesheetTable.clientProject")}
                        queryType='assignedProjects'
                        queryParams={{ idEmployee: userDetails?.entityId, endDate: date }}
                        groupBy={(option: Project) => option?.client?.name || ''}
                        getOptionKey={(option => option?.id)}
                        getOptionLabel={(option: Project) => `${option?.name}`}
                        renderValue={(option: Project) => <Box display={"flex"} alignItems="center" sx={{}}>
                            {option?.client && (
                                <Chip
                                    variant="filled"
                                    label={option.client.name}
                                    size="small"
                                />
                            )}
                            <Typography
                                variant="h6"
                                title={option?.name}
                                sx={{ ml: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '150px' }}
                            >
                                {option?.name}
                            </Typography>
                            </Box>
                        }
                        selectOnFocus
                        value={key.idProject}
                        disabled={key.idProject!==undefined && !key.uuId}
                        disableClearable
                        fullWidth
                        onChange={(e, value) => handleProjectChange(key, value)}
                    />
                    <AutocompleteStored
                        label={intl.get("timesheetTable.task")}
                        queryType='taskByEmployeeAndProject'
                        queryParams={{ idEmployee: userDetails?.entityId, idProject: key.idProject }}
                        getOptionKey={(option => option?.id)}
                        groupBy={(option: Task) => option?.job?.description || ''}
                        getOptionLabel={(option) => option?.description || ''}
                        renderValue={(option: Task) => <Box display={"flex"} alignItems="center" sx={{}}>
                             {option?.job && (
                                <Chip
                                  variant="filled"
                                  label={option.job.description}
                                  size="small"
                                />
                              )}
                            <Typography variant="h6" title={option?.description} sx={{ ml: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '150px' }}>
                            {option?.description}
                            </Typography>
                            </Box>}
                        disabled={key.idProject===undefined || (!key.uuId && key.idTask!==undefined)}
                        value={key.idTask}
                        disableClearable
                        fullWidth
                        onChange={(e, value) => handleTaskChange(key, value)}
                        filterIds={getUsedTaskIds(key.uuId)}
                    />
                    {!isTimesheetLoading?
                        <TimesheetEditModal
                            disabled={key.uuId!==undefined}
                            timesheet={currentTimesheet}
                            onComplete={() => {refetchTimesheetSet()}}
                        />
                    :
                        <Skeleton variant="rounded" width="100%" height={40} animation="pulse"/>
                    }
                </Stack>
                <Divider sx={{ mb: 2 }} />
                </>)
            })}
        </Box>
        </Box>)
}
